import { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input, Label, Checkbox } from "@cieda/djayma-ui";
import { Save, Loader2 } from "lucide-react";

import { DEFAULT_COD_CONFIG, getCodConfig, type CodConfig } from "@/lib/cod";
import { REGIONS } from "@/lib/constants";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const CodSettings = () => {
  const { t } = useTranslation("cod");

  const [enabled, setEnabled] = useState(DEFAULT_COD_CONFIG.enabled);
  const [minAmount, setMinAmount] = useState(String(DEFAULT_COD_CONFIG.minAmount));
  const [maxAmount, setMaxAmount] = useState(String(DEFAULT_COD_CONFIG.maxAmount));
  const [allowedZones, setAllowedZones] = useState<string[]>([
    ...DEFAULT_COD_CONFIG.allowedZones,
  ]);
  const [requirePhoneVerification, setRequirePhoneVerification] = useState(
    DEFAULT_COD_CONFIG.requirePhoneVerification,
  );
  const [maxPendingOrders, setMaxPendingOrders] = useState(
    String(DEFAULT_COD_CONFIG.maxPendingOrders),
  );

  useEffect(() => {
    let cancelled = false;
    getCodConfig().then((cfg) => {
      if (cancelled) return;
      setEnabled(cfg.enabled);
      setMinAmount(String(cfg.minAmount));
      setMaxAmount(String(cfg.maxAmount));
      setAllowedZones([...cfg.allowedZones]);
      setRequirePhoneVerification(cfg.requirePhoneVerification);
      setMaxPendingOrders(String(cfg.maxPendingOrders));
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleZoneToggle = useCallback(
    (zoneId: string, checked: boolean) => {
      setAllowedZones((prev) =>
        checked ? [...prev, zoneId] : prev.filter((z) => z !== zoneId),
      );
      setSaved(false);
    },
    [],
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaved(false);

    // Build immutable config object
    const _updatedConfig: CodConfig = {
      enabled,
      minAmount: parseInt(minAmount, 10) || 0,
      maxAmount: parseInt(maxAmount, 10) || 0,
      allowedZones: [...allowedZones],
      requirePhoneVerification,
      maxPendingOrders: parseInt(maxPendingOrders, 10) || 1,
    };

    // MOCK: simulate API call
    await new Promise((resolve) => setTimeout(resolve, 600));

    if (import.meta.env.DEV) console.log("[MOCK] Saved CoD config:", _updatedConfig);

    setSaving(false);
    setSaved(true);
  }, [
    enabled,
    minAmount,
    maxAmount,
    allowedZones,
    requirePhoneVerification,
    maxPendingOrders,
  ]);

  return (
    <div className="mx-auto max-w-2xl space-y-8 rounded-xl border border-border bg-card p-6 md:p-8">
      {/* Title */}
      <div>
        <h2 className="font-display text-2xl font-semibold text-foreground">
          {t("admin.title")}
        </h2>
      </div>

      {/* Enable toggle */}
      <div className="flex items-center gap-3">
        <Checkbox
          id="cod-enabled"
          checked={enabled}
          onCheckedChange={(checked) => {
            setEnabled(checked === true);
            setSaved(false);
          }}
        />
        <Label htmlFor="cod-enabled" className="cursor-pointer">
          {t("admin.enabled")}
        </Label>
      </div>

      {/* Amount limits */}
      <div className="grid gap-6 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="cod-min-amount">{t("admin.min_amount")}</Label>
          <Input
            id="cod-min-amount"
            type="number"
            min={0}
            step={500}
            value={minAmount}
            onChange={(e) => {
              setMinAmount(e.target.value);
              setSaved(false);
            }}
            placeholder="2 000"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="cod-max-amount">{t("admin.max_amount")}</Label>
          <Input
            id="cod-max-amount"
            type="number"
            min={0}
            step={5000}
            value={maxAmount}
            onChange={(e) => {
              setMaxAmount(e.target.value);
              setSaved(false);
            }}
            placeholder="150 000"
          />
        </div>
      </div>

      {/* Zone selection */}
      <fieldset className="space-y-3">
        <legend className="text-sm font-medium text-foreground">
          {t("admin.zones")}
        </legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {REGIONS.map((region) => (
            <div key={region.id} className="flex items-center gap-2">
              <Checkbox
                id={`zone-${region.id}`}
                checked={allowedZones.includes(region.id)}
                onCheckedChange={(checked) =>
                  handleZoneToggle(region.id, checked === true)
                }
              />
              <Label htmlFor={`zone-${region.id}`} className="cursor-pointer">
                {region.label}
              </Label>
            </div>
          ))}
        </div>
      </fieldset>

      {/* Phone verification toggle */}
      <div className="flex items-center gap-3">
        <Checkbox
          id="cod-phone-verification"
          checked={requirePhoneVerification}
          onCheckedChange={(checked) => {
            setRequirePhoneVerification(checked === true);
            setSaved(false);
          }}
        />
        <Label htmlFor="cod-phone-verification" className="cursor-pointer">
          {t("admin.phone_verification")}
        </Label>
      </div>

      {/* Max pending orders */}
      <div className="max-w-xs space-y-2">
        <Label htmlFor="cod-max-pending">{t("admin.max_pending")}</Label>
        <Input
          id="cod-max-pending"
          type="number"
          min={1}
          max={10}
          value={maxPendingOrders}
          onChange={(e) => {
            setMaxPendingOrders(e.target.value);
            setSaved(false);
          }}
        />
      </div>

      {/* Save button */}
      <div className="flex items-center gap-3 pt-2">
        <Button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="bg-secondary text-secondary-foreground hover:bg-secondary/90"
        >
          {saving ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          {t("admin.save")}
        </Button>

        {saved && (
          <p className="text-sm text-green-600 dark:text-green-400">
            {t("admin.save")} !
          </p>
        )}
      </div>
    </div>
  );
};

export default CodSettings;
